import { useState } from "react";
import { useAxios } from "../../../utils/useAxios";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";

/**
 * @typedef {Object} ClassroomInfo
 * @property {number} id - The classroom ID
 * @property {string} name - Classroom name
 */

/**
 * @typedef {Object} StudentItem
 * @property {string} student_id - The student ID
 * @property {string} name - Student name
 * @property {string} lessons - Number of lessons completed (e.g., "00")
 * @property {"complete" | "incomplete"} status - Student completion status
 * @property {number} accuracy - Student accuracy percentage
 * @property {string} username - username of the student
 * @property {string} email - email of the student
 */

/**
 * @typedef {import("../../../common/types/types").PaginationMeta} PaginationMeta
 */

/**
 * @typedef {Object} ClassroomStudentsData
 * @property {ClassroomInfo} classroom - Classroom information
 * @property {StudentItem[]} students - List of students
 */

/**
 * @typedef {object} GetClassroomStudentsParams
 * @property {number} classroomId - Classroom ID (required)
 * @property {"all" | "complete" | "incomplete"} [status] - Filter by status
 * @property {number} [page] - Page number for pagination
 * @property {number} [per_page] - Number of items per page
 */

/**
 * Custom hook to fetch students list for a classroom with status filter and pagination
 */
export const useGetClassroomStudentsList = () => {
    const [data, setData] = useState(
        /** @type {ClassroomStudentsData | null | undefined} */(undefined),
    );
    const [meta, setMeta] = useState(
        /** @type {PaginationMeta | null | undefined} */(undefined),
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const { axiosInstance } = useAxios();

    /**
     * Fetch students for a classroom with status filter and pagination
     * @async
     * @param {GetClassroomStudentsParams} params
     * @returns {Promise<void>}
     */
    const getClassroomStudents = async ({
        classroomId,
        status = "all",
        page = 1,
        per_page = 15,
    }) => {
        try {
            setLoading(true);
            setError("");

            // --- Build Query Params ---
            const query = new URLSearchParams();

            // Status filter
            if (status) query.append("status", status);

            // Pagination
            query.append("page", String(page));
            query.append("per_page", String(per_page));

            const url = `${TENANT_DOMAIN}/classroom/${classroomId}/students?${query.toString()}`;

            const response = await axiosInstance.get(url);

            const responseData = response.data?.data;
            setData({
                classroom: responseData?.classroom || null,
                students: responseData?.students?.data || [],
            });
            setMeta({
                current_page: responseData?.students?.current_page,
                per_page: responseData?.students?.per_page,
                total: responseData?.students?.total,
                last_page: responseData?.students?.last_page,
            });
        } catch (err) {
            setData(null);
            setMeta(null);

            const errorCode = err?.response?.data?.error_code;
            const errorMessage = err?.response?.data?.message;

            if (
                err?.response?.status === 500 ||
                err?.status === 500 ||
                errorCode === "INTERNAL_SERVER_ERROR"
            ) {
                setError(errorMessage || ERROR_MESSAGES.INTERNAL_SERVER);
            } else {
                setError(errorMessage || ERROR_MESSAGES.UNKNOWN);
            }
        } finally {
            setLoading(false);
        }
    };

    return {
        data,
        meta,
        loading,
        error,
        getClassroomStudents,
    };
};